import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import styled from '@emotion/styled';
import { settingsTheme as t } from '../../theme';

export interface PaginationLabels {
  /** "Mostrando" */
  showing: string;
  /** "de" */
  of: string;
  /** "Página" */
  page: string;
  /** aria-label del botón anterior */
  prev: string;
  /** aria-label del botón siguiente */
  next: string;
}

interface PaginationProps {
  page: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  labels: PaginationLabels;
  onChange: (page: number) => void;
}

const Bar = styled.div({
  marginTop: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  flexWrap: 'wrap',
  fontFamily: t.font,
  fontSize: 13.5,
  color: t.color.muted,
});

const Controls = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const PageLabel = styled.span({
  color: t.color.text,
  fontWeight: 500,
});

const NavButton = styled.button({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  borderRadius: t.radius.xs,
  border: `1px solid ${t.color.inputBorder}`,
  background: t.color.surface,
  color: t.color.text,
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, color 0.15s ease, background 0.15s ease',
  '& .MuiSvgIcon-root': { fontSize: 20 },
  '&:hover:not(:disabled)': { borderColor: t.color.primary, color: t.color.primary },
  '&:disabled': { opacity: 0.45, cursor: 'not-allowed' },
});

/**
 * Molécula: barra de paginación. A la izquierda el rango mostrado, a la derecha
 * el indicador de página y los botones anterior/siguiente. Los números se
 * pintan directo en el JSX (sin pasar por params de traducción).
 */
const Pagination = ({ page, pageSize, totalPages, totalItems, labels, onChange }: PaginationProps) => {
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  return (
    <Bar>
      <span>
        {labels.showing} {from}–{to} {labels.of} {totalItems}
      </span>
      <Controls>
        <PageLabel>
          {labels.page} {page} {labels.of} {totalPages}
        </PageLabel>
        <NavButton type="button" aria-label={labels.prev} disabled={page <= 1} onClick={() => onChange(page - 1)}>
          <ChevronLeft />
        </NavButton>
        <NavButton
          type="button"
          aria-label={labels.next}
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          <ChevronRight />
        </NavButton>
      </Controls>
    </Bar>
  );
};

export default Pagination;
